// Single place that formats timestamps in the agent's selected timezone.
//
// The user picks a tz in Settings (defaulting to the browser's resolved zone).
// Every visible date/time across the dashboard goes through these helpers so
// switching tz is a one-place change.

import { useAgentStore } from '../stores/agent';

/** All IANA tz names live in `Intl.supportedValuesOf('timeZone')`. */
export function supportedTimeZones(): string[] {
  const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
  if (typeof fn === 'function') {
    try {
      return fn('timeZone');
    } catch {
      /* fall through */
    }
  }
  // Browser without the helper — fall back to a curated common-zone list.
  return ['UTC', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles', 'Europe/London'];
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function tz(): string {
  return useAgentStore.getState().timezone || browserTimeZone();
}

/**
 * The zone every timestamp in this app is rendered in — the agent's chosen
 * one, falling back to their browser's. Exported so surfaces that compare
 * against it (the visitor's local clock) use the same answer the timestamps
 * beside them do, rather than reading the browser directly and disagreeing
 * with the rest of the screen whenever the agent has overridden it.
 */
export function displayTimeZone(): string {
  return tz();
}

const TIME_FMT_CACHE = new Map<string, Intl.DateTimeFormat>();
function timeFmt(zone: string): Intl.DateTimeFormat {
  let fmt = TIME_FMT_CACHE.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: zone });
    TIME_FMT_CACHE.set(zone, fmt);
  }
  return fmt;
}

const DATETIME_FMT_CACHE = new Map<string, Intl.DateTimeFormat>();
function dateTimeFmt(zone: string): Intl.DateTimeFormat {
  let fmt = DATETIME_FMT_CACHE.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat([], {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: zone,
    });
    DATETIME_FMT_CACHE.set(zone, fmt);
  }
  return fmt;
}

const DATE_FMT_CACHE = new Map<string, Intl.DateTimeFormat>();
function dateFmt(zone: string): Intl.DateTimeFormat {
  let fmt = DATE_FMT_CACHE.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat([], { year: 'numeric', month: 'short', day: '2-digit', timeZone: zone });
    DATE_FMT_CACHE.set(zone, fmt);
  }
  return fmt;
}

export function formatTime(ts: number | Date): string {
  return timeFmt(tz()).format(ts);
}

export function formatDateTime(ts: number | Date): string {
  return dateTimeFmt(tz()).format(ts);
}

export function formatDate(ts: number | Date): string {
  return dateFmt(tz()).format(ts);
}

/** "5 Jun 2026" if same year is omitted differently — keeping it simple. */
export function formatRelativeOrAbsolute(ts: number | Date): string {
  const now = Date.now();
  const when = typeof ts === 'number' ? ts : ts.getTime();
  const ms = now - when;
  if (ms < 60_000) return 'just now';
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 12 * 60 * 60_000) return `${Math.floor(ms / (60 * 60_000))}h ago`;
  return formatDateTime(when);
}
